// Basic Three.js Boat Simulator
// This is a starting point for creating a boat simulator like the one in the image

import * as THREE from 'three';
import { Water } from 'three/addons/objects/Water.js';
import { Sky } from 'three/addons/objects/Sky.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { createMap } from './map.js';
import nipplejs from 'nipplejs';
import { NPCManager } from './npcManager.js';
import { PortalManager } from './portal.js';
import {
    INITIAL_HEALTH,
    MAX_EXPLOSIONS,
    CANNON_COOLDOWN,
    CANNON_COUNT,
    CANNON_SPREAD,
    MAX_CANNON_BALLS,
    FRAME_INTERVAL,
    START_POSITION,
    PORTAL_POSITION,
    NPCState
} from './constants.js';

// Global WebSocket connection
window.ws = null;

// Main variables
let scene, camera, renderer;
let water, sun, sky;
let playerBoat;
let islands = [];
let controls;
let clock = new THREE.Clock();
let keyboard = {};
let cannonBalls = [];
let npcManager;  // Add npcManager declaration
let portalManager; // Add portalManager declaration

// Replace the existing cannonSound declaration at the top
let cannonSound = null;

// Add at the top with other variables
let lastFrameTime = 0;
let frameCount = 0; // Add frame counter for water update optimization

// Add at the top with other variables
let playerId;
const otherPlayers = new Map();

// Add at the top with other variables
const SERVER_DOMAIN = import.meta.env.VITE_SERVER_DOMAIN;
if (!SERVER_DOMAIN) {
    throw new Error('SERVER_DOMAIN is not defined');
}

// Add at the top with other variables
let explosions = [];

// Add at the top with other variables
let backgroundMusic = null;
let secondBackgroundMusic = null;
let thirdBackgroundMusic = null;
let currentMusicTrack = 1; // Track which song is currently playing

// Add at the top with other variables
let isMusicMuted = false;

// Add at the top with other variables
let boatsDestroyed = 0;
let gameStartTime = 0;
let playTime = 0;

// Add at the top with other variables
const destroyedBoats = new Map();

// Add at the top with other variables
let playerName = '';

// Add at the top with other variables
let isCameraControlActive = false;
let cameraDistance = 150;
let cameraHeight = 80;
let cameraRotation = 0;
let lastMouseX = 0;
let lastMouseY = 0;
let isMouseDown = false;

// Add a new variable for the water bubbles sound
let waterBubblesSound = null;

// Add a keyboard state object to track key states
const keyState = {
    w: false,
    s: false,
    a: false,
    d: false,
    q: false,
    e: false,
    ArrowUp: false,
    ArrowDown: false,
    ArrowLeft: false,
    ArrowRight: false
};

// Add formatTime function
function formatTime(seconds) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

// Update the initializeAudio function
function initializeAudio() {
    // Create an audio listener and add it to the camera
    const listener = new THREE.AudioListener();
    camera.add(listener);

    // Create a sound for the cannon
    cannonSound = new THREE.Audio(listener);

    // Load a sound and set it as the Audio object's buffer
    const audioLoader = new THREE.AudioLoader();
    audioLoader.load('./audio/cannon-fire-161072.mp3', function (buffer) {
        cannonSound.setBuffer(buffer);
        cannonSound.setVolume(0.15); // Reduced from 0.4 to 0.25 for quieter cannon sound
        console.log("Cannon sound loaded successfully");
    }, undefined, function (err) {
        console.error("Error loading cannon sound:", err);
        // Try loading a fallback sound
        audioLoader.load('https://assets.mixkit.co/active_storage/sfx/2771/2771-preview.mp3', function (buffer) {
            cannonSound.setBuffer(buffer);
            cannonSound.setVolume(0.2); // Reduced from 0.3 to 0.2 for quieter fallback sound
            console.log("Fallback cannon sound loaded");
        });
    });

    // Create and load water bubbles sound for boat destruction
    waterBubblesSound = new THREE.Audio(listener);
    audioLoader.load('./audio/water-bubbles-257594.mp3', function (buffer) {
        waterBubblesSound.setBuffer(buffer);
        waterBubblesSound.setVolume(0.3);
        console.log("Water bubbles sound loaded successfully");
    }, undefined, function (err) {
        console.error("Error loading water bubbles sound:", err);
    });
}

// Background music tracks by gabrieleromanoy
// https://pixabay.com/users/28454482/?tab=music&order=latest&pagi=1
function initializeBackgroundMusic() {
    if (backgroundMusic === null) {
        // Initialize all three tracks
        backgroundMusic = new Audio('./audio/ambient-synth-272816.mp3');
        secondBackgroundMusic = new Audio('./audio/soft-synth-romantic-274644.mp3');
        thirdBackgroundMusic = new Audio('./audio/relax-synth-275097.mp3');

        // Set volume for all tracks
        backgroundMusic.volume = 0.2;
        secondBackgroundMusic.volume = 0.2;
        thirdBackgroundMusic.volume = 0.2;

        // Preload all tracks
        backgroundMusic.load();
        secondBackgroundMusic.load();
        thirdBackgroundMusic.load();

        // Randomly select first track to play (1, 2, or 3)
        currentMusicTrack = Math.floor(Math.random() * 3) + 1;

        // Set up event listeners for the first track
        backgroundMusic.addEventListener('ended', () => {
            console.debug('First track ended, switching to second track');
            currentMusicTrack = 2;
            if (!isMusicMuted) {
                secondBackgroundMusic.play()
                    .then(() => console.debug('Second track started playing'))
                    .catch(e => console.error('Error playing second track:', e));
            }
        });

        // Set up event listeners for the second track
        secondBackgroundMusic.addEventListener('ended', () => {
            console.debug('Second track ended, switching to third track');
            currentMusicTrack = 3;
            if (!isMusicMuted) {
                thirdBackgroundMusic.play()
                    .then(() => console.debug('Third track started playing'))
                    .catch(e => console.error('Error playing third track:', e));
            }
        });

        // Set up event listeners for the third track
        thirdBackgroundMusic.addEventListener('ended', () => {
            console.debug('Third track ended, switching back to first track');
            currentMusicTrack = 1;
            if (!isMusicMuted) {
                backgroundMusic.play()
                    .then(() => console.debug('First track started playing again'))
                    .catch(e => console.error('Error playing first track:', e));
            }
        });

        // Show any loading errors
        const tracks = [backgroundMusic, secondBackgroundMusic, thirdBackgroundMusic];
        tracks.forEach((track, index) => {
            track.addEventListener('error', (e) => {
                console.error(`Error loading background music track ${index + 1}:`, e);
            });
        });
    }
}


// Add createGameStats function
function createGameStats() {
    // Create kill counter
    const killCounter = document.createElement('div');
    killCounter.id = 'killCounter';
    killCounter.style.position = 'fixed';
    killCounter.style.top = '20px';
    killCounter.style.left = '20px';
    killCounter.style.color = 'white';
    killCounter.style.fontSize = '20px';
    killCounter.style.fontFamily = 'Arial, sans-serif';
    killCounter.style.textShadow = '2px 2px 2px rgba(0,0,0,0.5)';
    killCounter.innerHTML = 'Boats Destroyed: 0';
    document.body.appendChild(killCounter);

    // Create time played counter
    const timeCounter = document.createElement('div');
    timeCounter.id = 'timeCounter';
    timeCounter.style.position = 'fixed';
    timeCounter.style.top = '45px';
    timeCounter.style.left = '20px';
    timeCounter.style.color = 'white';
    timeCounter.style.fontSize = '14px';
    timeCounter.style.fontFamily = 'Arial, sans-serif';
    timeCounter.style.textShadow = '2px 2px 2px rgba(0,0,0,0.5)';
    timeCounter.innerHTML = 'Time: 0:00';
    document.body.appendChild(timeCounter);

    // Create leaderboard button
    const leaderboardBtn = document.createElement('button');
    leaderboardBtn.style.position = 'fixed';
    leaderboardBtn.style.top = '70px'; // Moved down to accommodate time counter
    leaderboardBtn.style.left = '20px';
    leaderboardBtn.style.padding = '10px 20px';
    leaderboardBtn.style.backgroundColor = '#4CAF50';
    leaderboardBtn.style.color = 'white';
    leaderboardBtn.style.border = 'none';
    leaderboardBtn.style.borderRadius = '5px';
    leaderboardBtn.style.cursor = 'pointer';
    leaderboardBtn.innerHTML = 'Leaderboard';
    leaderboardBtn.onclick = showLeaderboard;
    document.body.appendChild(leaderboardBtn);
}

// Update the startGame function to properly initialize music
function startGame() {
    console.log("--------------------------------Starting game--------------------------------");
    const startPrompt = document.getElementById('startPrompt');
    const nameInput = document.getElementById('playerNameInput');

    // Check if we're coming from a portal
    const urlParams = new URLSearchParams(window.location.search);
    const isFromPortal = urlParams.get('portal') === 'true';

    // Only check name input if not coming from portal
    if (!isFromPortal && !nameInput.value.trim()) {
        nameInput.style.border = '2px solid red';
        return;
    }

    // Set player name - either from portal or input
    if (!isFromPortal) {
        playerName = nameInput.value.trim();
    }

    // Reset keyboard state to prevent any stuck keys
    keyboard = {};

    // Reset game stats
    boatsDestroyed = 0;
    gameStartTime = Date.now();
    document.getElementById('killCounter').innerHTML = 'Boats Destroyed: 0';

    // Initialize and play background music with random first track
    initializeBackgroundMusic();

    if (currentMusicTrack === 1 && backgroundMusic) {
        setTimeout(() => {
            backgroundMusic.play()
                .then(() => {
                    isMusicMuted = false;
                })
                .catch(e => {
                    console.log("Music play failed:", e);
                    document.addEventListener('click', () => {
                        backgroundMusic.play()
                            .then(() => {
                                isMusicMuted = false;
                            })
                            .catch(e => console.log("Music play failed after user interaction:", e));
                    }, { once: true });
                });
        }, 100);
    } else if (currentMusicTrack === 2 && secondBackgroundMusic) {
        setTimeout(() => {
            secondBackgroundMusic.play()
                .then(() => {
                    isMusicMuted = false;
                })
                .catch(e => {
                    console.log("Music play failed:", e);
                    document.addEventListener('click', () => {
                        secondBackgroundMusic.play()
                            .then(() => {
                                isMusicMuted = false;
                            })
                            .catch(e => console.log("Music play failed after user interaction:", e));
                    }, { once: true });
                });
        }, 100);
    } else if (currentMusicTrack === 3 && thirdBackgroundMusic) {
        setTimeout(() => {
            thirdBackgroundMusic.play()
                .then(() => {
                    isMusicMuted = false;
                })
                .catch(e => {
                    console.log("Music play failed:", e);
                    document.addEventListener('click', () => {
                        thirdBackgroundMusic.play()
                            .then(() => {
                                isMusicMuted = false;
                            })
                            .catch(e => console.log("Music play failed after user interaction:", e));
                    }, { once: true });
                });
        }, 100);
    }

    // Initialize audio for sound effects
    initializeAudio();

    // Create portals
    portalManager.createStartPortal();
    portalManager.createExitPortal();
    portalManager.createVibeMetaversePortal();

    const initData = {
        position: isFromPortal ? PORTAL_POSITION : START_POSITION,
        rotation: { x: 0, y: 0, z: 0 },
    };
    // Send join message using the existing WebSocket connection
    if (window.ws && window.ws.readyState === WebSocket.OPEN) {
        window.ws.send(JSON.stringify({
            type: 'join',
            position: initData.position,
            rotation: initData.rotation,
            name: playerName
        }));
        // createPlayerBoat(initData);
    } else {
        console.error('WebSocket connection not ready');
    }
}

// Add this function after initializeBackgroundMusic
function createUIControls() {
    const controlPanel = document.createElement('div');
    controlPanel.style.position = 'fixed';
    controlPanel.style.right = '20px';
    controlPanel.style.top = '20px';  // Changed from 50% to 20px to position at left
    controlPanel.style.transform = 'none';  // Removed translateY
    controlPanel.style.display = 'flex';
    controlPanel.style.flexDirection = 'column';
    controlPanel.style.gap = '10px';
    controlPanel.style.zIndex = '1000';

    // Music toggle button
    const musicButton = document.createElement('button');
    musicButton.style.width = '40px';
    musicButton.style.height = '40px';
    musicButton.style.border = 'none';
    musicButton.style.borderRadius = '5px';
    musicButton.style.background = '#333';
    musicButton.style.color = 'white';
    musicButton.style.cursor = 'pointer';
    musicButton.style.display = 'flex';
    musicButton.style.alignItems = 'center';
    musicButton.style.justifyContent = 'center';
    musicButton.innerHTML = '🔊';
    musicButton.title = 'Toggle Music';

    // Prevent focus and handle click
    musicButton.addEventListener('mousedown', (e) => {
        e.preventDefault();  // Prevent default focus behavior

        // Toggle music mute state
        isMusicMuted = !isMusicMuted;

        // Apply mute state to both music tracks
        if (backgroundMusic) {
            backgroundMusic.muted = isMusicMuted;
        }
        if (secondBackgroundMusic) {
            secondBackgroundMusic.muted = isMusicMuted;
        }
        if (thirdBackgroundMusic) {
            thirdBackgroundMusic.muted = isMusicMuted;
        }

        // Update button icon
        musicButton.innerHTML = isMusicMuted ? '🔇' : '🔊';
    });
    // Remove focus on mouse up
    musicButton.addEventListener('mouseup', () => musicButton.blur());

    // Question mark button (enabled now)
    const helpButton = document.createElement('button');
    helpButton.style.width = '40px';
    helpButton.style.height = '40px';
    helpButton.style.border = 'none';
    helpButton.style.borderRadius = '5px';
    helpButton.style.background = '#333';
    helpButton.style.color = 'white';
    helpButton.style.cursor = 'pointer';
    helpButton.style.opacity = '1';
    helpButton.innerHTML = '?';
    helpButton.title = 'Show Instructions';

    // Show instructions on click
    helpButton.addEventListener('mousedown', (e) => {
        e.preventDefault();
        const instructionsPrompt = document.getElementById('instructionsPrompt');
        if (instructionsPrompt) {
            instructionsPrompt.style.display = 'block';

            // Add click listener to close instructions
            const closeInstructions = (e) => {
                if (e.target === instructionsPrompt || instructionsPrompt.contains(e.target)) {
                    instructionsPrompt.style.display = 'none';
                    document.removeEventListener('click', closeInstructions);
                }
            };

            // Add slight delay to prevent immediate closing
            setTimeout(() => {
                document.addEventListener('click', closeInstructions);
            }, 100);
        }
    });

    // Remove focus on mouse up
    helpButton.addEventListener('mouseup', () => helpButton.blur());

    // Add hover effects
    [musicButton, helpButton].forEach(element => {
        element.style.transition = 'background-color 0.2s';
        element.addEventListener('mouseover', () => {
            element.style.backgroundColor = '#444';
        });
        element.addEventListener('mouseout', () => {
            element.style.backgroundColor = '#333';
        });
    });

    controlPanel.appendChild(musicButton);
    controlPanel.appendChild(helpButton);
    document.body.appendChild(controlPanel);

    // Update the instructions prompt to include camera controls
    const instructionsPrompt = document.getElementById('instructionsPrompt');
    if (instructionsPrompt) {
        const controlsDiv = instructionsPrompt.querySelector('.controls');
        if (controlsDiv) {
            controlsDiv.innerHTML = `
                <p>• W/S : Move forwards/backwards</p>
                <p>• A/D : Turn left/right</p>
                <p>• Q : Fire left cannons</p>
                <p>• E : Fire right cannons</p>
                <p>• Arrow keys : Control camera position</p>
                <p>• Click and drag mouse : Rotate and adjust camera</p>
                <p>• Mouse wheel : Zoom camera in/out</p>
                <p>• Yellow bars show cannon reload status</p>
                <p>• Green bar shows ship health</p>
            `;
        }
    }
}

// Add debug function for keyboard state
function debugKeyboardState() {
    const pressedKeys = Object.keys(keyboard).filter(key => keyboard[key] === true);
    if (pressedKeys.length > 0) {
        console.log('Currently pressed keys:', pressedKeys);
    } else {
        console.log('No keys currently pressed');
    }
}

// Make the debug function available globally
window.debugKeyboardState = debugKeyboardState;

// Initialize the scene
async function init() {
    // Create scene
    scene = new THREE.Scene();
    window.scene = scene;

    // Initialize portal manager
    portalManager = new PortalManager(scene);

    // Initialize background music
    initializeBackgroundMusic();

    // Create UI controls
    createUIControls();

    createGameStats();

    // Check if we're coming from a portal
    const urlParams = new URLSearchParams(window.location.search);
    const isFromPortal = urlParams.get('portal') === 'true';
    const portalUsername = urlParams.get('username');

    // Set up event listeners for starting the game
    const startPrompt = document.getElementById('startPrompt');
    const nameInput = document.getElementById('playerNameInput');

    // Create camera with position matching where boats will spawn
    camera = new THREE.PerspectiveCamera(
        40,
        window.innerWidth / window.innerHeight,
        1,
        20000
    );
    // Position camera at boat spawn position (0, 0, 200) with height and offset
    camera.position.set(0, 80, 350);
    camera.lookAt(0, 0, 200);

    // Create renderer with optimized settings
    renderer = new THREE.WebGLRenderer({
        antialias: false, // Disable antialiasing for better performance
        powerPreference: 'high-performance'
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5)); // Limit pixel ratio for better performance
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2; // Increased from 0.5 to 1.2 for brighter overall scene

    // Enable shadows on the renderer
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    document.body.appendChild(renderer.domElement);

    // Add controls
    controls = new OrbitControls(camera, renderer.domElement);
    controls.maxPolarAngle = Math.PI * 0.495;
    controls.minDistance = 10;
    controls.maxDistance = 100;

    // Disable orbit controls since we're using camera follow
    controls.enabled = false;

    // Handle window resize
    window.addEventListener('resize', onWindowResize);

    // Setup keyboard controls
    setupKeyboardControls();

    // Create scene elements
    createLighting();
    createSky();
    createWater();
    createMap(scene);
    createWindEffect();

    // Initialize WebSocket connection and set up message handlers
    const socket = initializeWebSocket();
    socket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        console.log("Received message:", data);

        switch (data.type) {
            case 'init':
                // Store player ID
                playerId = data.id;
                playerBoat = createPlayerBoat(data)

                // Initialize NPC Manager with scene and playerBoat reference
                npcManager = new NPCManager(scene, playerBoat);

                // Add other players
                data.players.forEach(player => {
                    if (player.id !== playerId) {
                        createOtherPlayerBoat(player);
                    }
                });

                // // Add NPCs
                data.npcs.forEach(npc => {
                    createNPCBoat(npc);
                });

                // Add destroyed boats
                data.destroyedBoats.forEach(boat => {
                    createDestroyedBoat(boat);
                });
                break;

            case 'newPlayer':
                createOtherPlayerBoat(data.player);
                showNotification(`Player ${data.player.name} joins the game`);
                break;

            case 'playerUpdate':
                updateOtherPlayerBoat(data.player);
                break;

            case 'playerDisconnect':
                const playerDisconnectBoat = otherPlayers.get(data.playerId);
                showNotification(`Player ${playerDisconnectBoat.userData.name} left the game`);
                removeOtherPlayerBoat(data.playerId);
                break;

            case 'cannon':
                // Use sourceId instead of playerId to identify the firing boat
                const firingBoat = otherPlayers.get(data.sourceId) || (npcManager ? npcManager.npcShips.get(data.sourceId) : null);
                if (firingBoat) {
                    createCannonEffect(data.sourceId, data.side);
                }
                break;

            case 'hit':
                // Handle hit effects - create explosion at hit position if available
                const targetBoat = identifyBoat(data.targetId);
                if (targetBoat && data.hitPosition) {
                    // Convert hit position to world coordinates
                    const hitPos = new THREE.Vector3(
                        targetBoat.position.x + data.hitPosition.x,
                        targetBoat.position.y + data.hitPosition.y,
                        targetBoat.position.z + data.hitPosition.z
                    );
                    // Create explosion at hit position
                    createExplosion(hitPos);
                }
                break;

            case 'health':
                let healthBoat = identifyBoat(data.targetId);
                if (data.health <= 0 && healthBoat.health > 0) {
                    showNotification(`Player ${data.name} was destroyed!`);
                }
                updateBoatHealth(data.targetId, data.health);
                break;

            case 'leaderboard':
                updateLeaderboard(data);
                break;

            case 'npcUpdate':
                if (npcManager) {
                    npcManager.handleNPCUpdate(data.npc);
                }
                break;

            case 'npcHealth':
                if (npcManager) {
                    npcManager.handleNPCHealthUpdate(data.npcId, data.health);
                }
                break;

            case 'npcHit':
                if (npcManager) {
                    npcManager.handleNPCHit(data.npcId, data.hitType, data.hitPosition);
                }
                break;
        }
    };

    // Add after other initialization code
    if (isMobile) {
        setupMobileControls();
    }

    try {
        // Load the shared boat model before starting the game
        await loadSharedBoatModel();

        if (isFromPortal) {
            // If coming from portal, skip the start prompt and use portal username or generate random name
            playerName = portalUsername || 'Player' + Math.floor(Math.random() * 1000);
            if (startPrompt) {
                startPrompt.style.display = 'none';
            }
            startGame();
        }
    } catch (error) {
        console.error('Error loading shared boat model:', error);
    }

    // Start animation loop
    animate();
}

// Add the animate function before init()
function animate() {
    requestAnimationFrame(animate);

    // Limit frame rate
    const currentTime = Date.now();
    const elapsed = currentTime - lastFrameTime;

    if (elapsed > FRAME_INTERVAL) {
        lastFrameTime = currentTime - (elapsed % FRAME_INTERVAL);

        // Always update water and sky, regardless of game state
        const time = performance.now() * 0.0005;
        water.material.uniforms['time'].value = time;

        // Update sun position
        const phi = THREE.MathUtils.degToRad(75);
        const theta = THREE.MathUtils.degToRad(180 + time * 2);
        sun.setFromSphericalCoords(1, phi, theta);
        sky.material.uniforms['sunPosition'].value.copy(sun);
        water.material.uniforms['sunDirection'].value.copy(sun).normalize();

        // Update wind particles
        updateWindParticles();

        frameCount++;

        // Game-specific updates only when player boat exists
        if (playerBoat && playerBoat.health > 0) {
            updatePlayerBoat();

            // Update time played counter less frequently (every 10 frames)
            if (frameCount % 10 === 0 && gameStartTime > 0) {
                const currentPlayTime = (Date.now() - gameStartTime) / 1000;
                document.getElementById('timeCounter').innerHTML = `Time: ${formatTime(currentPlayTime)}`;
            }

            // Update cannon balls with improved physics
            updateCannonBalls();

            // Only check collisions every other frame to improve performance
            if (frameCount % 2 === 0) {
                cleanCannonBalls();
            }

            // Update explosions with particle effects
            updateExplosions();

            // Update reload indicators less frequently (every 5 frames)
            if (frameCount % 5 === 0 && playerBoat.healthBar) {
                const currentTime = Date.now();

                // Update left cannon reload indicator
                if (playerBoat.healthBar.leftReloadIndicator) {
                    const leftProgress = Math.min(1, (currentTime - playerBoat.lastCannonFiredLeft) / CANNON_COOLDOWN);
                    playerBoat.healthBar.leftReloadIndicator.scale.x = leftProgress;
                }

                // Update right cannon reload indicator
                if (playerBoat.healthBar.rightReloadIndicator) {
                    const rightProgress = Math.min(1, (currentTime - playerBoat.lastCannonFiredRight) / CANNON_COOLDOWN);
                    playerBoat.healthBar.rightReloadIndicator.scale.x = rightProgress;
                }
            }

            // Update health bars to face camera less frequently (every 3 frames)
            if (frameCount % 3 === 0) {
                if (playerBoat.healthBar) {
                    playerBoat.healthBar.lookAt(camera.position);
                }

                // Only update visible boats' health bars
                otherPlayers.forEach(boat => {
                    if (boat.healthBar && boat.health > 0) {
                        boat.healthBar.lookAt(camera.position);
                    }
                });
            }

            // Update NPCs
            if (npcManager) {
                npcManager.update(playerBoat);
            }
        }

        // Always update portals
        if (portalManager) {
            portalManager.animate();

            // Only check portal collisions if player boat exists
            if (playerBoat) {
                portalManager.checkExitPortalCollision(playerBoat);
                portalManager.checkVibeMetaversePortalCollision(playerBoat);
            }
        }

        // Always render the scene
        renderer.render(scene, camera);
    }
}

// Create sky with sunset/sunrise effect
function createSky() {
    sky = new Sky();
    sky.scale.setScalar(10000); // Restored to original value
    scene.add(sky);

    sun = new THREE.Vector3();

    const skyUniforms = sky.material.uniforms;
    // Adjust for bright tropical sky - restored original values
    skyUniforms['turbidity'].value = 1.5; // Even lower for crystal clear sky
    skyUniforms['rayleigh'].value = 0.5; // Lower for more intense blue
    skyUniforms['mieCoefficient'].value = 0.002; // Less atmospheric haze
    skyUniforms['mieDirectionalG'].value = 0.95; // Sharper sun disk

    // Position sun higher for tropical midday sun
    const phi = THREE.MathUtils.degToRad(75); // Higher sun position
    const theta = THREE.MathUtils.degToRad(180);

    sun.setFromSphericalCoords(1, phi, theta);
    skyUniforms['sunPosition'].value.copy(sun);
}

// Create realistic water
function createWater() {
    const waterGeometry = new THREE.PlaneGeometry(2000, 2000, 32, 32); // Reduced size from 6000 to 2000

    water = new Water(
        waterGeometry,
        {
            textureWidth: 512,
            textureHeight: 512,
            waterNormals: new THREE.TextureLoader().load('https://threejs.org/examples/textures/waternormals.jpg', function (texture) {
                texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
            }),
            sunDirection: new THREE.Vector3(),
            sunColor: 0xffffff,
            waterColor: 0x001e0f,
            distortionScale: 3.7,
            fog: scene.fog !== undefined
        }
    );

    water.rotation.x = -Math.PI / 2;
    water.position.y = -2; // Slightly lower to avoid z-fighting with beaches
    scene.add(water);
}

// Create lighting for the scene
function createLighting() {
    const ambientLight = new THREE.AmbientLight(0x404040, 3); // Increased intensity from 2 to 3
    scene.add(ambientLight);

    const directionalLight = new THREE.DirectionalLight(0xffa95c, 2); // Reduced intensity from 4 to 2 and kept warm color
    directionalLight.position.set(-1, 1, 1);
    scene.add(directionalLight);
}

// Modify the createHealthBar function
function createHealthBar(isPlayer = false, name = '') {
    const healthBarGroup = new THREE.Group();

    // Player name text
    if (name) {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = 1024; // Increased from 512
        canvas.height = 256; // Increased from 128

        context.font = 'bold 128px Arial'; // Increased from 64px
        context.fillStyle = 'white';
        context.textAlign = 'center';
        context.fillText(name, canvas.width / 2, canvas.height / 2);

        const texture = new THREE.CanvasTexture(canvas);
        const nameMaterial = new THREE.SpriteMaterial({
            map: texture,
            depthTest: false, // Ensure name is always visible
            depthWrite: false
        });
        const nameSprite = new THREE.Sprite(nameMaterial);
        nameSprite.position.set(0, 3, -2); // Added z-offset to put name in front
        nameSprite.scale.set(16, 4, 1); // Increased from (8, 2, 1)
        nameSprite.renderOrder = 999; // Ensure it renders on top
        healthBarGroup.add(nameSprite);
    }

    // Background (black bar)
    const bgGeometry = new THREE.PlaneGeometry(6, 0.5);
    const bgMaterial = new THREE.MeshBasicMaterial({
        color: 0x000000,
        side: THREE.DoubleSide,
        depthTest: true
    });
    const background = new THREE.Mesh(bgGeometry, bgMaterial);
    background.renderOrder = 1;

    // Health indicator (green bar)
    const healthGeometry = new THREE.PlaneGeometry(6, 0.5);
    const healthMaterial = new THREE.MeshBasicMaterial({
        color: 0x00ff00,
        side: THREE.DoubleSide,
        depthTest: false
    });
    const healthIndicator = new THREE.Mesh(healthGeometry, healthMaterial);
    healthIndicator.renderOrder = 2;

    // Add meshes to group
    healthBarGroup.add(background);
    healthBarGroup.add(healthIndicator);

    // For player boat only, add reload indicators
    if (isPlayer) {
        // Left cannon reload bar (background)
        const leftReloadBg = new THREE.Mesh(
            new THREE.PlaneGeometry(2.9, 0.25),
            new THREE.MeshBasicMaterial({
                color: 0x000000,
                side: THREE.DoubleSide,
                depthTest: true
            })
        );
        leftReloadBg.position.set(-1.55, -0.4, 0);
        leftReloadBg.renderOrder = 1;
        healthBarGroup.add(leftReloadBg);

        // Left cannon reload indicator (yellow)
        const leftReloadIndicator = new THREE.Mesh(
            new THREE.PlaneGeometry(2.9, 0.25),
            new THREE.MeshBasicMaterial({
                color: 0xffff00,
                side: THREE.DoubleSide,
                depthTest: false
            })
        );
        leftReloadIndicator.position.set(-1.55, -0.4, 0);
        leftReloadIndicator.renderOrder = 2;
        healthBarGroup.add(leftReloadIndicator);

        // Right cannon reload bar (background)
        const rightReloadBg = new THREE.Mesh(
            new THREE.PlaneGeometry(2.9, 0.25),
            new THREE.MeshBasicMaterial({
                color: 0x000000,
                side: THREE.DoubleSide,
                depthTest: true
            })
        );
        rightReloadBg.position.set(1.55, -0.4, 0);
        rightReloadBg.renderOrder = 1;
        healthBarGroup.add(rightReloadBg);

        // Right cannon reload indicator (yellow)
        const rightReloadIndicator = new THREE.Mesh(
            new THREE.PlaneGeometry(2.9, 0.25),
            new THREE.MeshBasicMaterial({
                color: 0xffff00,
                side: THREE.DoubleSide,
                depthTest: false
            })
        );
        rightReloadIndicator.position.set(1.55, -0.4, 0);
        rightReloadIndicator.renderOrder = 2;
        healthBarGroup.add(rightReloadIndicator);

        // Store references to reload indicators
        healthBarGroup.leftReloadIndicator = leftReloadIndicator;
        healthBarGroup.rightReloadIndicator = rightReloadIndicator;
    }

    // Position the health bar above the boat
    healthBarGroup.position.set(0, 15, 0);

    // Rotate health bar to be horizontal for player, vertical for others
    if (isPlayer) {
        healthBarGroup.rotation.x = Math.PI / 2;
    } else {
        healthBarGroup.rotation.x = Math.PI / 2;
        healthBarGroup.rotation.z = Math.PI / 2;
    }

    // Add a reference to the health indicator for updating
    healthBarGroup.healthIndicator = healthIndicator;

    return healthBarGroup;
}

// Modify the createBoat function to remove shininess property
// function createBoat(isPlayer = false, name = '') {
//     const boat = new THREE.Group();

//     // Add health property
//     boat.health = INITIAL_HEALTH;

//     // Add cooldown timers specific to this boat
//     boat.lastCannonFiredLeft = 0;
//     boat.lastCannonFiredRight = 0;

//     // Add ID to userData
//     boat.userData.id = isPlayer ? 'player' : 'other_' + Math.random().toString(36).substr(2, 9);

//     // Hull - different color for player vs other players
//     const hullGeometry = new THREE.BoxGeometry(8, 6, 25);
//     const hullMaterial = new THREE.MeshStandardMaterial({
//         color: isPlayer ? 0x8B4513 : 0xA0522D,  // Lighter brown colors for better visibility
//         roughness: 0.7,  // Reduced roughness for more light reflection
//         metalness: 0.1   // Slight metalness for better light response
//     });
//     const hull = new THREE.Mesh(hullGeometry, hullMaterial);
//     hull.position.y = 3;
//     boat.add(hull);

//     // Add health bar with isPlayer parameter and name
//     const healthBar = createHealthBar(isPlayer, name);
//     boat.add(healthBar);
//     boat.healthBar = healthBar;

//     // Gun ports
//     const gunPortGeometry = new THREE.BoxGeometry(0.5, 0.5, 0.2);
//     const gunPortMaterial = new THREE.MeshStandardMaterial({ color: 0x222222 }); // Lighter black for gun ports

//     // Add gun ports on both sides
//     for (let deck = 1; deck <= 3; deck++) {
//         for (let i = -8; i <= 8; i += 2) {
//             // Port side
//             const portGunLeft = new THREE.Mesh(gunPortGeometry, gunPortMaterial);
//             portGunLeft.position.set(-4.1, deck * 1.5, i);
//             hull.add(portGunLeft);

//             // Starboard side
//             const portGunRight = new THREE.Mesh(gunPortGeometry, gunPortMaterial);
//             portGunRight.position.set(4.1, deck * 1.5, i);
//             hull.add(portGunRight);
//         }
//     }

//     // Multiple masts
//     const mastPositions = [-8, 0, 8];
//     mastPositions.forEach((zPos) => {
//         const mastGeometry = new THREE.CylinderGeometry(0.3, 0.4, 20);
//         const mastMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
//         const mast = new THREE.Mesh(mastGeometry, mastMaterial);
//         mast.position.set(0, 13, zPos);

//         // Sails
//         const sailHeights = [5, 12, 19];
//         sailHeights.forEach(height => {
//             const sailWidth = 8;
//             const sailHeight = 7;
//             const sailGeometry = new THREE.PlaneGeometry(sailWidth, sailHeight, 10, 1);

//             // Add curve to the sail
//             const positions = sailGeometry.attributes.position.array;
//             for (let i = 0; i < positions.length; i += 3) {
//                 const x = positions[i];
//                 positions[i] -= Math.sin(x / sailWidth * Math.PI) * 0.7;
//             }

//             const sailMaterial = new THREE.MeshStandardMaterial({
//                 color: isPlayer ? 0xFFFFFF : 0xF5F5F5,  // Pure white for player, slightly off-white for others
//                 side: THREE.DoubleSide,
//                 emissive: 0x444444,  // Increased emissive for better visibility
//                 roughness: 0.5,      // Reduced roughness
//                 metalness: 0.1
//             });

//             const sail = new THREE.Mesh(sailGeometry, sailMaterial);
//             sail.rotation.y = Math.PI / 2;
//             sail.position.set(0, height, zPos);
//             boat.add(sail);
//         });

//         boat.add(mast);
//     });

//     // Add bowsprit
//     const bowspritGeometry = new THREE.CylinderGeometry(0.2, 0.3, 10);
//     const bowsprit = new THREE.Mesh(bowspritGeometry, hullMaterial);
//     bowsprit.position.set(0, 8, -13);
//     bowsprit.rotation.x = Math.PI / 4;
//     boat.add(bowsprit);

//     boat.add(hull);

//     // Set initial position on water surface (y=0)
//     if (isPlayer) {
//         // Check if we're coming from a portal
//         const urlParams = new URLSearchParams(window.location.search);
//         const isFromPortal = urlParams.get('portal') === 'true';

//         if (isFromPortal) {
//             // Position at start portal location
//             boat.position.set(PORTAL_POSITION.x, PORTAL_POSITION.y, PORTAL_POSITION.z);
//         } else {
//             // Normal starting position
//             boat.position.set(START_POSITION.x, START_POSITION.y, START_POSITION.z);
//         }
//     } else {
//         // Random position for other boats, but always on water surface
//         const x = (Math.random() - 0.5) * 800;
//         const z = (Math.random() - 0.5) * 800;
//         boat.position.set(x, 0, z);
//     }

//     return boat;
// }

// Function to create player's boat
function createPlayerBoat(data) {
    // Create a temporary boat group
    const boat = new THREE.Group();
    boat.health = INITIAL_HEALTH;
    boat.lastCannonFiredLeft = 0;
    boat.lastCannonFiredRight = 0;
    boat.userData.id = data.id;
    scene.add(boat);

    // Add health bar with isPlayer parameter and name
    const healthBar = createHealthBar(true, playerName);
    boat.add(healthBar);
    boat.healthBar = healthBar;

    // Clone and add the shared boat model if it exists
    if (sharedBoatModel) {
        const modelClone = sharedBoatModel.clone();
        boat.add(modelClone);
    }

    // Set position and rotation from init data if provided
    boat.position.set(data.position.x, data.position.y, data.position.z);
    boat.rotation.set(data.rotation.x, data.rotation.y, data.rotation.z);

    return boat;
}

// Function to create other player's boat
function createOtherPlayerBoat(player) {
    // Create a temporary boat group
    const boat = new THREE.Group();
    boat.health = INITIAL_HEALTH;
    boat.lastCannonFiredLeft = 0;
    boat.lastCannonFiredRight = 0;
    boat.userData.id = player.id;
    boat.userData.name = player.name;
    scene.add(boat);

    // Add health bar with isPlayer parameter and name
    const healthBar = createHealthBar(false, player.name);
    boat.add(healthBar);
    boat.healthBar = healthBar;

    // Clone and add the shared boat model if it exists
    if (sharedBoatModel) {
        const modelClone = sharedBoatModel.clone();
        boat.add(modelClone);
    }

    // Set position
    boat.position.copy(player.position);

    // Properly handle rotation - only set y rotation to avoid quaternion issues
    if (player.rotation && typeof player.rotation.y === 'number') {
        boat.rotation.y = player.rotation.y;
    }

    otherPlayers.set(player.id, boat);

    return boat;
}

// Add new function to handle destroyed state
function applyDestroyedState(boat) {
    boat.health = 0;
    if (boat.healthBar && boat.healthBar.healthIndicator) {
        boat.healthBar.healthIndicator.scale.x = 0;
    }

    // Darken the boat color and add damage effects
    boat.traverse((child) => {
        if (child.isMesh) {
            // Create a new material to avoid affecting other boats
            if (child.material.clone) {
                child.material = child.material.clone();
                child.material.color.setHex(0x444444);
                if (child.material.map) {
                    child.material.emissive.setHex(0x330000);
                }
            }
        }
    });

    // Sink and tilt the boat
    boat.position.y = -3;
    boat.rotation.z = Math.PI * 0.15;
}

function updateOtherPlayerBoat(player) {
    console.log("Received update for player:", player.id, "with position:", player.position);
    const boat = otherPlayers.get(player.id);
    if (boat) {
        // Only update position and rotation if the boat isn't destroyed
        if (boat.health > 0) {
            // Update position
            boat.position.set(player.position.x, player.position.y, player.position.z);

            // Properly handle rotation - only set y rotation to avoid quaternion issues
            if (player.rotation && typeof player.rotation.y === 'number') {
                boat.rotation.y = player.rotation.y;
            }
        }

        // Update health if provided
        if (player.health !== undefined) {
            // If health reaches 0, apply destroyed state
            if (player.health <= 0 && boat.health > 0) {
                applyDestroyedState(boat);
            } else if (player.health > 0) {
                boat.health = player.health;
                boat.healthBar.healthIndicator.scale.x = player.health / INITIAL_HEALTH;
            }
        }
    } else {
        console.warn("Boat not found for player ID:", player.id);
    }
}

// Handle window resize
function onWindowResize() {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
}

// Update player boat based on keyboard input
function updatePlayerBoat() {
    const moveSpeed = 2.0; // Adjusted from 2.0 to 1.6 as requested
    const rotateSpeed = 0.05; // Increased from 0.02
    let hasMoved = false;

    // Forward/backward - only move when keys are actually pressed
    if (keyState.w && playerBoat && playerBoat.health > 0) {
        playerBoat.translateZ(-moveSpeed);
        hasMoved = true;
    }
    if (keyState.s && playerBoat && playerBoat.health > 0) {
        playerBoat.translateZ(moveSpeed);
        hasMoved = true;
    }

    // Rotation - only rotate when keys are actually pressed
    if (keyState.a && playerBoat && playerBoat.health > 0) {
        playerBoat.rotation.y += rotateSpeed;
        hasMoved = true;
    }
    if (keyState.d && playerBoat && playerBoat.health > 0) {
        playerBoat.rotation.y -= rotateSpeed;
        hasMoved = true;
    }

    // Keep boat on water level
    if (playerBoat) {
        playerBoat.position.y = 0;
    }

    // Send position update if the boat moved or every 100ms
    if (window.ws && window.ws.readyState === 1) {
        if (!playerBoat.userData.lastUpdate || hasMoved && (Date.now() - playerBoat.userData.lastUpdate > 100)) {
            window.ws.send(JSON.stringify({
                id: playerBoat.userData.id,
                type: 'update',
                position: {
                    x: playerBoat.position.x,
                    y: playerBoat.position.y,
                    z: playerBoat.position.z
                },
                rotation: {
                    y: playerBoat.rotation.y
                }
            }));
            playerBoat.userData.lastUpdate = Date.now();
        }
    }

    // Update camera position based on current camera parameters
    if (playerBoat) {
        const cameraX = playerBoat.position.x + Math.sin(cameraRotation) * cameraDistance;
        const cameraZ = playerBoat.position.z + Math.cos(cameraRotation) * cameraDistance;
        camera.position.set(cameraX, cameraHeight, cameraZ);

        // Always look at the boat's position plus some height
        const lookAtPoint = playerBoat.position.clone();
        lookAtPoint.y += 20;
        camera.lookAt(lookAtPoint);
    }
}

// Update water animation
function updateWater() {
    water.material.uniforms['time'].value += 1.0 / 60.0;
}

// Modify the fireCannonBroadside function to fix the userData.lastFired bug
function fireCannonBroadside(side, sourceBoat = playerBoat) {
    // Check if cannons are ready
    const now = Date.now();

    // Initialize userData if it doesn't exist
    if (!sourceBoat.userData) {
        sourceBoat.userData = {};
    }

    // Track last fired time for each side separately
    if (side === 'left') {
        if (!sourceBoat.lastCannonFiredLeft) {
            sourceBoat.lastCannonFiredLeft = 0;
        }

        if (now - sourceBoat.lastCannonFiredLeft < CANNON_COOLDOWN) {
            return; // Cannons are still reloading
        }

        // Update last fired time
        sourceBoat.lastCannonFiredLeft = now;
    } else {
        if (!sourceBoat.lastCannonFiredRight) {
            sourceBoat.lastCannonFiredRight = 0;
        }

        if (now - sourceBoat.lastCannonFiredRight < CANNON_COOLDOWN) {
            return; // Cannons are still reloading
        }

        // Update last fired time
        sourceBoat.lastCannonFiredRight = now;
    }

    // Play cannon sound only once (simplified from multiple plays)
    if (cannonSound && cannonSound.buffer) {
        // Play sound once with proper volume
        if (cannonSound.isPlaying) {
            cannonSound.stop(); // Stop if already playing
        }
        cannonSound.play();
    }

    // Create cannon positions if they don't exist yet
    if (!sourceBoat.userData.leftCannons) {
        sourceBoat.userData.leftCannons = [];
        sourceBoat.userData.rightCannons = [];

        // Create cannon positions along the sides of the boat
        for (let i = -10; i <= 10; i += 1) {
            const leftPos = new THREE.Vector3(-4, 3, i);
            const rightPos = new THREE.Vector3(4, 3, i);
            sourceBoat.userData.leftCannons.push(leftPos);
            sourceBoat.userData.rightCannons.push(rightPos);
        }
    }

    // Create cannon balls
    const cannonPositions = side === 'left' ? sourceBoat.userData.leftCannons : sourceBoat.userData.rightCannons;

    // Only create a subset of cannon balls to avoid performance issues
    const maxBalls = Math.min(CANNON_COUNT, cannonPositions.length);

    // Ensure we don't exceed the maximum number of cannon balls
    if (cannonBalls.length > MAX_CANNON_BALLS - maxBalls) {
        // Remove oldest cannon balls if we're approaching the limit
        cannonBalls.splice(0, maxBalls);
    }

    // Array to collect all cannonball positions
    const allPositions = [];

    // Create cannon balls
    for (let i = 0; i < maxBalls; i++) {
        const cannonPosition = cannonPositions[i].clone();

        // Convert local position to world position
        sourceBoat.localToWorld(cannonPosition);

        // Create cannon ball
        const cannonBall = new THREE.Mesh(
            new THREE.SphereGeometry(0.5, 8, 8),
            new THREE.MeshPhongMaterial({ color: 0x000000 })
        );
        cannonBall.position.copy(cannonPosition);

        // Set direction based on boat's rotation and side
        const direction = new THREE.Vector3(0, 0, 0);
        if (side === 'left') {
            direction.set(-1, 0, 0);
        } else {
            direction.set(1, 0, 0);
        }

        // Add some randomness to the direction
        direction.x += (Math.random() - 0.5) * CANNON_SPREAD;
        direction.z += (Math.random() - 0.5) * CANNON_SPREAD;

        // Apply boat's rotation to the direction
        direction.applyQuaternion(sourceBoat.quaternion);

        // Store direction and other data with the cannon ball
        cannonBall.userData = {
            direction: direction,
            speed: 18 + Math.random() * 12, // Doubled again (was 9 + Math.random() * 6)
            sourceId: sourceBoat.userData.id,
            createdAt: now
        };

        scene.add(cannonBall);
        cannonBalls.push(cannonBall);
        
        // Collect position for the WebSocket message
        allPositions.push({
            x: cannonPosition.x,
            y: cannonPosition.y,
            z: cannonPosition.z
        });
    }
    
    // Send all positions in a single WebSocket message
    if ((sourceBoat === playerBoat || (npcManager && npcManager.npcShips.has(sourceBoat.userData.id))) && allPositions.length > 0) {
        window.ws.send(JSON.stringify({
            type: 'cannonPosition',
            side: side,
            id: sourceBoat.userData.id,
            positions: allPositions
        }));
    }
}

// Update gravity for even slower fall
function updateCannonBalls() {
    const gravity = new THREE.Vector3(0, -0.015, 0);
    const currentTime = Date.now();

    // Pre-calculate values outside the loop for better performance
    const removeTime = currentTime - 3000;

    // Use a more efficient filtering approach
    let i = 0;
    while (i < cannonBalls.length) {
        const ball = cannonBalls[i];

        // Update position based on direction and speed
        ball.position.addScaledVector(ball.userData.direction, ball.userData.speed);
        ball.position.y -= 0.015; // Apply gravity directly instead of creating a new vector

        // Remove balls that are too old or below water
        if (ball.userData.createdAt < removeTime || ball.position.y < 0) {
            // Remove from scene and swap with last element for O(1) removal
            scene.remove(ball);

            // Fast removal without creating new arrays
            cannonBalls[i] = cannonBalls[cannonBalls.length - 1];
            cannonBalls.pop();
        } else {
            i++;
        }
    }
}

// Modify cleanCannonBalls to be more efficient
function cleanCannonBalls() {
    // Only process if we have cannonballs
    if (cannonBalls.length === 0) return;

    // Process each cannon ball
    let i = 0;
    while (i < cannonBalls.length) {
        const ball = cannonBalls[i];

        // Remove cannon balls that are too old or below water
        if (ball.userData.createdAt < Date.now() - 3000 || ball.position.y < 0) {
            scene.remove(ball);
            cannonBalls[i] = cannonBalls[cannonBalls.length - 1];
            cannonBalls.pop();
        } else {
            i++;
        }
    }
}

// Add these functions before updateBoatHealth
function createExplosion(position) {
    // Limit total number of concurrent explosions
    if (explosions.length >= MAX_EXPLOSIONS) {
        const oldest = explosions.shift();
        scene.remove(oldest);
    }

    // Reduce particle count for better performance
    const particleCount = 10; // Reduced from 15
    const explosionGroup = new THREE.Group();
    explosionGroup.position.copy(position);

    // Use a single geometry and material for all particles of the same type
    const debrisGeometry = new THREE.SphereGeometry(0.15);
    const debrisMaterial = new THREE.MeshBasicMaterial({ color: 0xFF4500 });

    const smokeGeometry = new THREE.SphereGeometry(0.4);
    const smokeMaterial = new THREE.MeshBasicMaterial({
        color: 0x888888,
        transparent: true,
        opacity: 0.5
    });

    // Create debris particles
    for (let i = 0; i < particleCount; i++) {
        // Debris (orange-red particles)
        const debris = new THREE.Mesh(debrisGeometry, debrisMaterial);

        // Simplified random positioning
        const angle = (i / particleCount) * Math.PI * 2;
        const radius = 1;
        debris.position.set(
            Math.cos(angle) * radius,
            Math.random() * 2,
            Math.sin(angle) * radius
        );

        // Simplified velocity calculation
        debris.velocity = new THREE.Vector3(
            debris.position.x,
            1 + Math.random(),
            debris.position.z
        ).normalize().multiplyScalar(0.5);

        explosionGroup.add(debris);
    }

    // Create smoke particles (grey) - fewer smoke particles
    for (let i = 0; i < particleCount / 3; i++) {
        const smoke = new THREE.Mesh(smokeGeometry, smokeMaterial);

        // Simplified positioning
        const angle = (i / (particleCount / 3)) * Math.PI * 2;
        const radius = 0.5;
        smoke.position.set(
            Math.cos(angle) * radius,
            Math.random(),
            Math.sin(angle) * radius
        );

        // Simplified velocity
        smoke.velocity = new THREE.Vector3(0, 0.3, 0);
        smoke.scale.set(1, 1, 1);
        explosionGroup.add(smoke);
    }

    explosionGroup.created = Date.now();
    scene.add(explosionGroup);
    explosions.push(explosionGroup);
}

function updateExplosions() {
    const currentTime = Date.now();
    const gravity = new THREE.Vector3(0, -0.03, 0);

    explosions = explosions.filter(explosion => {
        const age = currentTime - explosion.created;

        // Remove explosion after 1.5 seconds
        if (age > 1500) {
            scene.remove(explosion);
            return false;
        }

        // Only update particles every other frame for better performance
        if (frameCount % 2 === 0) {
            // Calculate fade factor once
            const fadeFactor = 1 - age / 1500;

            // Update each particle with simplified physics
            explosion.children.forEach(particle => {
                particle.position.add(particle.velocity);
                particle.velocity.add(gravity);

                // Simplified smoke effect
                if (particle.geometry.parameters.radius > 0.3) {
                    particle.scale.addScalar(0.02);
                    particle.material.opacity = Math.max(0, 0.5 * fadeFactor);
                }
            });
        }

        return true;
    });
}

function identifyBoat(playerId) {
    let boat;
    if (playerId === playerBoat?.userData?.id) {
        boat = playerBoat;
    } else {
        boat = otherPlayers.get(playerId);
    }
    if (!boat && npcManager) {
        boat = npcManager.npcShips.get(playerId);
    }
    return boat;
}

// Modify updateBoatHealth to reduce explosion count
function updateBoatHealth(playerId, newHealth) {
    // Get the boat reference
    let boat = identifyBoat(playerId);

    // Early return if boat not found
    if (!boat) {
        console.warn(`Boat not found for playerId: ${playerId}`);
        return;
    }

    // Store old health for damage calculation
    const oldHealth = boat.health;

    // Update boat's health
    boat.health = newHealth;

    // Create explosion if damage was taken
    if (newHealth < oldHealth) {
        // Create explosions proportional to damage taken
        const damage = oldHealth - newHealth;
        const explosionCount = Math.min(3, Math.ceil(damage / 10));

        for (let i = 0; i < explosionCount; i++) {
            const offset = new THREE.Vector3(
                (Math.random() - 0.5) * 4,
                2 + Math.random() * 2,
                (Math.random() - 0.5) * 20
            );
            const explosionPos = boat.position.clone().add(offset);
            createExplosion(explosionPos);
        }
    }

    // Update health bar if it exists
    if (boat.healthBar?.healthIndicator) {
        const newScale = newHealth / INITIAL_HEALTH;
        boat.healthBar.healthIndicator.scale.x = newScale;
    }

    // Handle boat destruction
    if (newHealth <= 0 && oldHealth > 0) {
        applyDestroyedState(boat);

        // Play water bubbles sound when a boat is destroyed
        if (waterBubblesSound?.buffer) {
            if (waterBubblesSound.isPlaying) {
                waterBubblesSound.stop();
            }
            waterBubblesSound.play();
        }

        // If it's another player's boat, move it to destroyedBoats
        if (boat !== playerBoat) {
            const boatId = Array.from(otherPlayers.entries()).find(([_, b]) => b === boat)?.[0];
            if (boatId) {
                destroyedBoats.set(boatId, boat);
                otherPlayers.delete(boatId);
            }
        }

        // Only show game over for the local player
        if (boat === playerBoat) {
            showGameOver();
        }

        // If this was a kill by the player
        if (playerBoat && boat !== playerBoat) {
            boatsDestroyed++;
            document.getElementById('killCounter').innerHTML = `Boats Destroyed: ${boatsDestroyed}`;
        }
    }
}

function showGameOver() {
    playTime = (Date.now() - gameStartTime) / 1000;

    // Play water bubbles sound for game over
    if (waterBubblesSound && waterBubblesSound.buffer) {
        if (waterBubblesSound.isPlaying) {
            waterBubblesSound.stop(); // Stop if already playing
        }
        waterBubblesSound.play();
    }

    const gameOverDiv = document.createElement('div');
    gameOverDiv.id = 'gameOverMessage';
    gameOverDiv.style.position = 'fixed';
    gameOverDiv.style.top = '50%';
    gameOverDiv.style.left = '50%';
    gameOverDiv.style.transform = 'translate(-50%, -50%)';
    gameOverDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
    gameOverDiv.style.padding = '30px';
    gameOverDiv.style.borderRadius = '10px';
    gameOverDiv.style.color = 'white';
    gameOverDiv.style.textAlign = 'center';
    gameOverDiv.style.minWidth = '400px';
    gameOverDiv.style.zIndex = '1000';

    // Title with player name
    const title = document.createElement('h1');
    title.style.color = '#0088ff';
    title.style.marginBottom = '20px';
    title.innerHTML = `Game Over ${playerName}!`;

    // Stats
    const stats = document.createElement('div');
    stats.style.marginBottom = '20px';
    stats.innerHTML = `
        <p>Boats Destroyed: ${boatsDestroyed}</p>
        <p>Time: ${formatTime(playTime)}</p>
    `;

    // Submit button
    const submitBtn = document.createElement('button');
    submitBtn.style.padding = '10px 20px';
    submitBtn.style.backgroundColor = '#4CAF50';
    submitBtn.style.color = 'white';
    submitBtn.style.border = 'none';
    submitBtn.style.borderRadius = '5px';
    submitBtn.style.cursor = 'pointer';
    submitBtn.style.marginRight = '10px';
    submitBtn.innerHTML = 'Play Again';
    submitBtn.onclick = () => {
        document.body.removeChild(gameOverDiv);
        resetGame();
    };

    gameOverDiv.appendChild(title);
    gameOverDiv.appendChild(stats);
    gameOverDiv.appendChild(submitBtn);
    document.body.appendChild(gameOverDiv);
}

function resetGame() {
    // Simply reload the page for a fresh start
    window.location.reload();
}

// Add this function at the top level
function initializeWebSocket() {
    if (window.ws && window.ws.readyState === WebSocket.OPEN) {
        return window.ws; // Return existing connection if it's open
    }

    // Connect to WebSocket server using WSS for secure connections
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const port = window.location.protocol === 'https:' ? '443' : '8080';
    window.ws = new WebSocket(`${protocol}//${SERVER_DOMAIN}:${port}/ws`);

    // Add heartbeat mechanism
    const heartbeat = {
        intervalId: null,
        start: function () {
            this.intervalId = setInterval(() => {
                if (window.ws.readyState === WebSocket.OPEN) {
                    window.ws.send(JSON.stringify({ type: 'ping' }));
                }
            }, 30000); // Send ping every 30 seconds
        },
        stop: function () {
            if (this.intervalId) {
                clearInterval(this.intervalId);
                this.intervalId = null;
            }
        }
    };

    window.ws.onopen = () => {
        console.log('Connected to server');
        heartbeat.start(); // Start sending heartbeat when connection opens
    };

    window.ws.onclose = () => {
        console.log('Disconnected from server');
        heartbeat.stop(); // Stop heartbeat when connection closes
        window.ws = null;
    };

    window.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        heartbeat.stop(); // Stop heartbeat on error
        window.ws = null;
    };

    return window.ws;
}

// Add updateLeaderboard function
function updateLeaderboard(data) {
    const leaderboardPanel = document.getElementById('leaderboardPanel');
    if (leaderboardPanel) {
        const table = leaderboardPanel.querySelector('table');
        if (table) {
            let tableHTML = `
                <tr style="background-color: #1a472a;">
                    <th style="padding: 10px;">Rank</th>
                    <th style="padding: 10px;">Player</th>
                    <th style="padding: 10px;">Boats</th>
                    <th style="padding: 10px;">Time</th>
                </tr>
            `;

            if (data.scores && data.scores.length > 0) {
                data.scores.forEach((score, index) => {
                    tableHTML += `
                        <tr style="border-bottom: 1px solid #333;">
                            <td style="padding: 10px; text-align: center;">${index + 1}</td>
                            <td style="padding: 10px;">${score.name}</td>
                            <td style="padding: 10px; text-align: center;">${score.boats_destroyed}</td>
                            <td style="padding: 10px; text-align: center;">${formatTime(score.time_played)}</td>
                        </tr>
                    `;
                });
            } else {
                tableHTML += `
                    <tr>
                        <td colspan="4" style="text-align: center; padding: 20px;">No scores yet</td>
                    </tr>
                `;
            }

            table.innerHTML = tableHTML;
        }
    }
}

// Modify showLeaderboard function
function showLeaderboard() {
    // Check if leaderboard is already open
    if (document.getElementById('leaderboardPanel')) {
        return; // Exit if leaderboard is already displayed
    }

    const leaderboardDiv = document.createElement('div');
    leaderboardDiv.id = 'leaderboardPanel';
    leaderboardDiv.style.position = 'fixed';
    leaderboardDiv.style.top = '50%';
    leaderboardDiv.style.left = '50%';
    leaderboardDiv.style.transform = 'translate(-50%, -50%)';
    leaderboardDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.9)';
    leaderboardDiv.style.padding = '30px';
    leaderboardDiv.style.borderRadius = '10px';
    leaderboardDiv.style.color = 'white';
    leaderboardDiv.style.textAlign = 'center';
    leaderboardDiv.style.minWidth = '400px';
    leaderboardDiv.style.zIndex = '1000';

    // Create title
    const title = document.createElement('h2');
    title.innerHTML = 'Leaderboard';
    leaderboardDiv.appendChild(title);

    // Create table
    const table = document.createElement('table');
    table.style.width = '100%';
    table.style.borderCollapse = 'collapse';
    table.style.marginTop = '20px';
    table.innerHTML = `
        <tr style="background-color: #1a472a;">
            <th style="padding: 10px;">Rank</th>
            <th style="padding: 10px;">Player</th>
            <th style="padding: 10px;">Boats</th>
            <th style="padding: 10px;">Time</th>
        </tr>
    `;
    leaderboardDiv.appendChild(table);

    // Create close button
    const closeButton = document.createElement('button');
    closeButton.style.padding = '10px 20px';
    closeButton.style.backgroundColor = '#4CAF50';
    closeButton.style.color = 'white';
    closeButton.style.border = 'none';
    closeButton.style.borderRadius = '5px';
    closeButton.style.cursor = 'pointer';
    closeButton.style.marginTop = '20px';
    closeButton.innerHTML = 'Close';
    closeButton.onclick = () => {
        document.body.removeChild(leaderboardDiv);
    };
    leaderboardDiv.appendChild(closeButton);

    // Add to document
    document.body.appendChild(leaderboardDiv);

    // Initialize or get existing WebSocket connection
    const socket = initializeWebSocket();

    // Request leaderboard data
    if (socket.readyState === WebSocket.OPEN) {
        socket.send(JSON.stringify({ type: 'getLeaderboard' }));
    } else {
        socket.addEventListener('open', () => {
            socket.send(JSON.stringify({ type: 'getLeaderboard' }));
        }, { once: true });
    }
}

// Function to create NPC boat
function createNPCBoat(npcData) {
    // Create a temporary boat group
    const npcBoat = new THREE.Group();
    npcBoat.health = INITIAL_HEALTH;
    npcBoat.lastCannonFiredLeft = 0;
    npcBoat.lastCannonFiredRight = 0;
    npcBoat.userData.id = npcData.id;
    npcBoat.userData.isNPC = true;
    npcBoat.userData.name = npcData.name;
    npcBoat.userData.state = NPCState.IDLE;
    scene.add(npcBoat);

    // Add health bar with isPlayer parameter and name
    const healthBar = createHealthBar(false, npcData.name);
    npcBoat.add(healthBar);
    npcBoat.healthBar = healthBar;

    // Clone and add the shared boat model if it exists
    if (sharedBoatModel) {
        const modelClone = sharedBoatModel.clone();
        npcBoat.add(modelClone);
    }

    // Set position and rotation from the npcData
    npcBoat.position.set(npcData.position.x, npcData.position.y, npcData.position.z);
    npcBoat.rotation.set(npcData.rotation.x, npcData.rotation.y, npcData.rotation.z);

    // Add to scene
    scene.add(npcBoat);

    // Add to NPCs map
    npcManager.npcShips.set(npcData.id, npcBoat);

    return npcBoat;
}

// Add mouse wheel event listener for camera zoom
document.addEventListener('wheel', (e) => {
    if (playerBoat) {
        // Throttle wheel events for better performance
        if (!window.wheelThrottled) {
            window.wheelThrottled = true;

            // Adjust camera distance with mouse wheel (reduced sensitivity)
            cameraDistance = Math.max(50, Math.min(400, cameraDistance + e.deltaY * 0.05));
            e.preventDefault();

            // Reset throttle after a short delay
            setTimeout(() => {
                window.wheelThrottled = false;
            }, 16); // Approximately 60fps
        }
    }
}, { passive: false });

// Add mouse event listeners for camera rotation and height control
document.addEventListener('mousedown', (e) => {
    if (playerBoat && playerBoat.health > 0) {
        isMouseDown = true;
        lastMouseX = e.clientX;
        lastMouseY = e.clientY;
    }
});

document.addEventListener('mousemove', (e) => {
    if (playerBoat && isMouseDown && playerBoat.health > 0) {
        // Throttle mouse move events for better performance
        if (!window.mouseMoveThrottled) {
            window.mouseMoveThrottled = true;

            // Calculate mouse movement delta
            const deltaX = e.clientX - lastMouseX;
            const deltaY = e.clientY - lastMouseY;

            // Update camera rotation based on horizontal mouse movement
            cameraRotation -= deltaX * 0.003;

            // Update camera height based on vertical mouse movement
            cameraHeight = Math.max(20, Math.min(200, cameraHeight - deltaY * 0.3));

            // Update last mouse position
            lastMouseX = e.clientX;
            lastMouseY = e.clientY;

            // Reset throttle after a short delay
            setTimeout(() => {
                window.mouseMoveThrottled = false;
            }, 16); // Approximately 60fps
        }
    }
});

document.addEventListener('mouseup', () => {
    isMouseDown = false;
});

// Improved keyboard event listeners
function setupKeyboardControls() {
    // Key down event
    window.addEventListener('keydown', (e) => {
        // Check if we're typing in the name input field
        const nameInput = document.getElementById('playerNameInput');
        const isTypingName = nameInput && document.activeElement === nameInput;
        const startPrompt = document.getElementById('startPrompt');
        const isGameActive = startPrompt && startPrompt.style.display === 'none';

        // If we're typing the name, only handle Enter key and return
        if (isTypingName) {
            if (e.key === 'Enter' && nameInput.value.trim()) {
                startPrompt.style.display = 'none';
                startGame();
            }
            return; // Exit early for all other keys when typing name
        }

        // Set the key state explicitly to true
        keyboard[e.key] = true;

        // Also track uppercase/lowercase variants to handle both cases
        if (e.key.toLowerCase() !== e.key.toUpperCase()) {
            keyboard[e.key.toLowerCase()] = true;
            keyboard[e.key.toUpperCase()] = true;
        }

        // Track key codes as well for special keys like Shift
        if (e.code) {
            keyboard[e.code] = true;
        }

        // Update keyState for movement controls
        if (e.key.toLowerCase() === 'w') keyState.w = true;
        if (e.key.toLowerCase() === 's') keyState.s = true;
        if (e.key.toLowerCase() === 'a') keyState.a = true;
        if (e.key.toLowerCase() === 'd') keyState.d = true;
        if (e.key.toLowerCase() === 'q') keyState.q = true;
        if (e.key.toLowerCase() === 'e') keyState.e = true;

        // Music toggle with 'm' key
        if (e.key.toLowerCase() === 'm') {
            // Toggle music mute state
            isMusicMuted = !isMusicMuted;

            // Apply mute state to all music tracks
            if (backgroundMusic) {
                backgroundMusic.muted = isMusicMuted;
            }
            if (secondBackgroundMusic) {
                secondBackgroundMusic.muted = isMusicMuted;
            }
            if (thirdBackgroundMusic) {
                thirdBackgroundMusic.muted = isMusicMuted;
            }

            // Update music button icon if it exists
            const musicButton = document.querySelector('button[title="Toggle Music"]');
            if (musicButton) {
                musicButton.innerHTML = isMusicMuted ? '🔇' : '🔊';
            }
        }

        // Only prevent default for game controls if the game is active and we're not typing a name
        if (isGameActive && !isTypingName &&
            ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'w', 'W', 'a', 'A', 's', 'S', 'd', 'D', 'q', 'Q', 'e', 'E', 'm', 'M'].includes(e.key)) {
            e.preventDefault();
        }

        // Only process game controls if the game is active
        if (isGameActive) {
            // Camera control with arrow keys (similar to mouse drag)
            // Only handle camera movement here, not boat movement
            if (playerBoat) {
                if (e.key === 'ArrowLeft') {
                    cameraRotation += 0.05; // Rotate camera left around boat
                }
                if (e.key === 'ArrowRight') {
                    cameraRotation -= 0.05; // Rotate camera right around boat
                }
                if (e.key === 'ArrowUp') {
                    cameraHeight = Math.min(cameraHeight + 5, 200); // Move camera up
                }
                if (e.key === 'ArrowDown') {
                    cameraHeight = Math.max(cameraHeight - 5, 20); // Move camera down
                }
            }

            // Fire cannons with Q and E keys (more intuitive for left/right)
            if (e.key === 'q' || e.key === 'Q') {
                if (cannonSound === null) {
                    initializeAudio();
                }
                fireCannonBroadside('left');
            }
            if (e.key === 'e' || e.key === 'E') {
                if (cannonSound === null) {
                    initializeAudio();
                }
                fireCannonBroadside('right');
            }

            // Keep the original Space and Shift controls as alternatives
            if (e.code === 'Space') {
                if (cannonSound === null) {
                    initializeAudio();
                }
                fireCannonBroadside('right');
            }
            if (e.code === 'ShiftLeft') {
                if (cannonSound === null) {
                    initializeAudio();
                }
                fireCannonBroadside('left');

                // Special handling for ShiftLeft to prevent stuck rotation
                // Store the current state of rotation keys before firing cannons
                const wasRotatingLeft = keyboard['a'] === true || keyboard['A'] === true;
                const wasRotatingRight = keyboard['d'] === true || keyboard['D'] === true;

                // After a short delay, restore the rotation state if keys are still pressed
                setTimeout(() => {
                    if (wasRotatingLeft && (keyboard['a'] === true || keyboard['A'] === true)) {
                        // Ensure rotation continues if the key is still pressed
                        keyboard['a'] = true;
                        keyboard['A'] = true;
                        keyState.a = true;
                    }
                    if (wasRotatingRight && (keyboard['d'] === true || keyboard['D'] === true)) {
                        // Ensure rotation continues if the key is still pressed
                        keyboard['d'] = true;
                        keyboard['D'] = true;
                        keyState.d = true;
                    }
                }, 50);
            }
        }
    });

    // Key up event
    window.addEventListener('keyup', (e) => {
        // Set the key state explicitly to false
        keyboard[e.key] = false;

        // Also clear uppercase/lowercase variants to handle both cases
        if (e.key.toLowerCase() !== e.key.toUpperCase()) {
            keyboard[e.key.toLowerCase()] = false;
            keyboard[e.key.toUpperCase()] = false;
        }

        // Clear key codes as well for special keys like Shift
        if (e.code) {
            keyboard[e.code] = false;
        }

        // Update keyState for movement controls
        if (e.key.toLowerCase() === 'w') keyState.w = false;
        if (e.key.toLowerCase() === 's') keyState.s = false;
        if (e.key.toLowerCase() === 'a') keyState.a = false;
        if (e.key.toLowerCase() === 'd') keyState.d = false;
        if (e.key.toLowerCase() === 'q') keyState.q = false;
        if (e.key.toLowerCase() === 'e') keyState.e = false;

        // Special handling for Shift key to prevent stuck rotation
        if (e.key === 'Shift' || e.code === 'ShiftLeft' || e.code === 'ShiftRight') {
            // When Shift is released, ensure rotation keys are properly reset
            // This prevents the boat from getting stuck rotating
            keyboard['a'] = false;
            keyboard['A'] = false;
            keyboard['d'] = false;
            keyboard['D'] = false;
            keyState.a = false;
            keyState.d = false;

            // Also reset Shift key states
            keyboard['Shift'] = false;
            keyboard['ShiftLeft'] = false;
            keyboard['ShiftRight'] = false;
        }
    });

    // Add window blur event listener to reset keyboard state when window loses focus
    window.addEventListener('blur', () => {
        // Reset all keyboard states when window loses focus
        keyboard = {};
        keyState.w = false;
        keyState.s = false;
        keyState.a = false;
        keyState.d = false;
        keyState.q = false;
        keyState.e = false;
    });

    // Add window focus event listener to ensure keyboard state is clean when window regains focus
    window.addEventListener('focus', () => {
        // Reset all keyboard states when window gains focus
        keyboard = {};
        keyState.w = false;
        keyState.s = false;
        keyState.a = false;
        keyState.d = false;
        keyState.q = false;
        keyState.e = false;
    });

    // Add visibility change event listener to reset keyboard state when tab is switched
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            // Reset all keyboard states when tab is not visible
            keyboard = {};
            keyState.w = false;
            keyState.s = false;
            keyState.a = false;
            keyState.d = false;
            keyState.q = false;
            keyState.e = false;
        }
    });
}

// Add at the top with other variables
let notifications = [];
const MAX_NOTIFICATIONS = 3; // Maximum number of notifications visible at once
const NOTIFICATION_DURATION = 5000; // How long notifications stay visible (5 seconds)

// Add function to create and show notifications
function showNotification(message, duration = 3000) {
    const container = document.getElementById('notificationContainer');
    const notification = document.createElement('div');
    notification.className = 'notification';
    notification.textContent = message;
    container.appendChild(notification);

    // Trigger reflow to enable transition
    notification.offsetHeight;
    notification.classList.add('show');

    // Remove notification after duration
    setTimeout(() => {
        notification.classList.remove('show');
        setTimeout(() => {
            container.removeChild(notification);
        }, 300);
    }, duration);
}

// Function to position notifications in a stack
function positionNotifications() {
    // Sort by creation time (oldest first)
    notifications.sort((a, b) => a.createdAt - b.createdAt);

    // Position each notification
    notifications.forEach((notification, index) => {
        const bottomPosition = 20 + (index * 60); // 20px initial bottom + 60px per notification
        notification.element.style.bottom = `${bottomPosition}px`;
        notification.element.style.left = '20px'; // Ensure all notifications are aligned to the left
    });

    // If we have too many notifications, remove the oldest ones
    while (notifications.length > MAX_NOTIFICATIONS) {
        const oldest = notifications.shift();
        if (oldest.element.parentNode) {
            oldest.element.parentNode.removeChild(oldest.element);
        }
    }
}

// Add at the top with other variables
let isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
let movementJoystick = null;
let cameraJoystick = null;
let lastTouchX = 0;
let lastTouchY = 0;
let isPinching = false;
let initialPinchDistance = 0;

// Add after the existing setupKeyboardControls function
function setupMobileControls() {
    if (!isMobile) return;

    // Movement Joystick
    movementJoystick = nipplejs.create({
        zone: document.getElementById('movementJoystick'),
        mode: 'static',
        position: { left: '50%', top: '50%' },
        color: 'white',
        size: 120,
        dynamicPage: true,
        fadeTime: 0
    });

    // Camera Joystick
    cameraJoystick = nipplejs.create({
        zone: document.getElementById('cameraJoystick'),
        mode: 'static',
        position: { left: '50%', top: '50%' },
        color: 'white',
        size: 120,
        dynamicPage: true,
        fadeTime: 0
    });

    // Movement Joystick Events
    movementJoystick.on('move', (evt, data) => {
        const angle = data.angle.radian;
        const force = Math.min(1, data.force / 2);

        // Convert angle and force to movement direction
        // Note: We subtract PI/2 from the angle to align with the desired directions
        // This makes up=0, right=PI/2, down=PI, left=-PI/2
        const adjustedAngle = angle - Math.PI / 2;
        const normalizedY = Math.cos(adjustedAngle) * force; // Forward/backward
        const normalizedX = Math.sin(adjustedAngle) * force; // Left/right

        // Update movement based on joystick position
        // Forward/backward movement (more sensitive)
        keyState.w = normalizedY > 0.01;
        keyState.s = normalizedY < -0.01;

        // Left/right rotation (less sensitive, requires more intentional movement)
        keyState.a = normalizedX > 0.1; // Increased threshold for left rotation
        keyState.d = normalizedX < -0.1;  // Increased threshold for right rotation
    });

    movementJoystick.on('end', () => {
        // Reset movement keys when joystick is released
        keyState.w = false;
        keyState.s = false;
        keyState.a = false;
        keyState.d = false;
    });

    // Camera Joystick Events
    cameraJoystick.on('move', (evt, data) => {
        const angle = data.angle.radian;
        const force = Math.min(1, data.force / 25); // Increased sensitivity for camera control

        // Convert angle and force to camera movement
        const normalizedX = Math.cos(angle) * force;
        const normalizedY = Math.sin(angle) * force;

        // Update camera based on joystick position with increased sensitivity
        cameraRotation += normalizedX * 0.1; // Doubled from 0.05 to 0.1
        cameraHeight = Math.max(30, Math.min(150, cameraHeight + normalizedY * 4)); // Doubled from 2 to 4
    });

    // Fire buttons
    const fireLeftButton = document.getElementById('fireLeft');
    const fireRightButton = document.getElementById('fireRight');

    // Fire button events
    fireLeftButton.addEventListener('touchstart', (e) => {
        e.preventDefault();
        if (cannonSound === null) {
            initializeAudio();
        }
        fireCannonBroadside('left');
    });

    fireRightButton.addEventListener('touchstart', (e) => {
        e.preventDefault();
        if (cannonSound === null) {
            initializeAudio();
        }
        fireCannonBroadside('right');
    });
}

// Make fireCannonBroadside available globally for NPC ships
window.fireCannonBroadside = fireCannonBroadside;

// Function to create a destroyed boat
function createDestroyedBoat(boatData) {
    // Create a temporary boat group
    const boat = new THREE.Group();
    boat.health = 0;
    boat.userData.id = boatData.id;
    scene.add(boat);

    // Add health bar with isPlayer parameter and name
    const healthBar = createHealthBar(false, boatData.name);
    boat.add(healthBar);
    boat.healthBar = healthBar;

    // Clone and add the shared boat model if it exists
    if (sharedBoatModel) {
        const modelClone = sharedBoatModel.clone();
        boat.add(modelClone);
    }

    // Set position and rotation from the boat data
    boat.position.set(boatData.position.x, boatData.position.y, boatData.position.z);
    boat.rotation.y = boatData.rotation.y;

    // Apply the destroyed state (sinking, tilting, darkening)
    applyDestroyedState(boat);

    // Store in destroyedBoats map
    destroyedBoats.set(boatData.id, boat);

    return boat;
}

// Add at the top with other variables
let sharedBoatModel = null;

// Add function to load the shared boat model
function loadSharedBoatModel() {
    const loader = new GLTFLoader();
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
    loader.setDRACOLoader(dracoLoader);

    return new Promise((resolve, reject) => {
        loader.load(
            './assets/boat.glb',
            (gltf) => {
                console.log('Shared boat model loaded successfully');
                const model = gltf.scene;

                // Center the model
                let box = new THREE.Box3().setFromObject(model);
                let center = box.getCenter(new THREE.Vector3());
                model.position.sub(center);

                // Rotate -90 degrees around Y axis
                model.rotation.y = Math.PI / 2;

                // Then calculate new bounds and center after rotation
                box = new THREE.Box3().setFromObject(model);
                center = box.getCenter(new THREE.Vector3());
                model.position.sub(center);

                // Adjust Y position of the model (raise it up)
                model.position.y += 20; // Increased from 10 to 150

                sharedBoatModel = model;
                resolve(model);
            },
            (progress) => {
                const percent = (progress.loaded / progress.total * 100).toFixed(2);
                console.log(`Loading shared boat model: ${percent}%`);
            },
            reject
        );
    });
}

// Add at the top with other variables
let windParticles;
let windParticleSystem;

// Add this function after createSky()
function createWindEffect() {
    // Create wind particles
    const particleCount = 2000; // Number of particles
    const windGeometry = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    const velocities = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);

    // Initialize particles
    for (let i = 0; i < particleCount * 3; i += 3) {
        // Random positions in the sky, but much lower
        positions[i] = (Math.random() - 0.5) * 2000; // x
        positions[i + 1] = Math.random() * 100 + 50; // y (much lower in the sky, between 50-150 units)
        positions[i + 2] = (Math.random() - 0.5) * 2000; // z

        // Consistent directional velocities (mostly moving in one direction with slight variations)
        velocities[i] = 1.5 + (Math.random() - 0.5) * 0.3; // x (positive direction with slight variation)
        velocities[i + 1] = (Math.random() - 0.5) * 0.1; // y (minimal vertical movement)
        velocities[i + 2] = (Math.random() - 0.5) * 0.3; // z (slight variation in z direction)

        // White/transparent colors with slight variation for depth effect
        const brightness = 0.7 + Math.random() * 0.3;
        colors[i] = brightness;
        colors[i + 1] = brightness;
        colors[i + 2] = brightness;
    }

    windGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    windGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    // Create particle material
    const windMaterial = new THREE.PointsMaterial({
        size: 1.5,
        vertexColors: true,
        transparent: true,
        opacity: 0.7,
        blending: THREE.AdditiveBlending
    });

    // Create particle system
    windParticleSystem = new THREE.Points(windGeometry, windMaterial);
    windParticles = {
        positions: positions,
        velocities: velocities
    };

    scene.add(windParticleSystem);
}

// Add this function to update wind particles
function updateWindParticles() {
    if (!windParticles) return;

    const positions = windParticles.positions;
    const velocities = windParticles.velocities;

    for (let i = 0; i < positions.length; i += 3) {
        // Update positions
        positions[i] += velocities[i];
        positions[i + 1] += velocities[i + 1];
        positions[i + 2] += velocities[i + 2];

        // Reset particles that go too far
        if (positions[i] > 1000) {
            positions[i] = -1000; // Reset to left side
            positions[i + 1] = Math.random() * 100 + 50; // Random height
            positions[i + 2] = (Math.random() - 0.5) * 2000; // Random z position
        }
    }

    windParticleSystem.geometry.attributes.position.needsUpdate = true;
}

// Function to create cannon effect for other players
function createCannonEffect(playerId, side) {
    const boat = otherPlayers.get(playerId);
    if (!boat) return;

    // Create cannon positions if they don't exist yet
    if (!boat.userData.leftCannons) {
        boat.userData.leftCannons = [];
        boat.userData.rightCannons = [];

        // Create cannon positions along the sides of the boat
        for (let i = -10; i <= 10; i += 1) {
            const leftPos = new THREE.Vector3(-4, 3, i);
            const rightPos = new THREE.Vector3(4, 3, i);
            boat.userData.leftCannons.push(leftPos);
            boat.userData.rightCannons.push(rightPos);
        }
    }

    // Get cannon positions based on side
    const cannonPositions = side === 'left' ? boat.userData.leftCannons : boat.userData.rightCannons;

    // Create cannon balls
    const maxBalls = Math.min(CANNON_COUNT, cannonPositions.length);

    // Ensure we don't exceed the maximum number of cannon balls
    if (cannonBalls.length > MAX_CANNON_BALLS - maxBalls) {
        // Remove oldest cannon balls if we're approaching the limit
        cannonBalls.splice(0, maxBalls);
    }

    // Create cannon balls
    for (let i = 0; i < maxBalls; i++) {
        const cannonPosition = cannonPositions[i].clone();

        // Convert local position to world position
        boat.localToWorld(cannonPosition);

        // Create cannon ball
        const cannonBall = new THREE.Mesh(
            new THREE.SphereGeometry(0.5, 8, 8),
            new THREE.MeshPhongMaterial({ color: 0x000000 })
        );
        cannonBall.position.copy(cannonPosition);

        // Set direction based on boat's rotation and side
        const direction = new THREE.Vector3(0, 0, 0);
        if (side === 'left') {
            direction.set(-1, 0, 0);
        } else {
            direction.set(1, 0, 0);
        }

        // Add some randomness to the direction
        direction.x += (Math.random() - 0.5) * CANNON_SPREAD;
        direction.z += (Math.random() - 0.5) * CANNON_SPREAD;

        // Apply boat's rotation to the direction
        direction.applyQuaternion(boat.quaternion);

        // Store direction and other data with the cannon ball
        cannonBall.userData = {
            direction: direction,
            speed: 18 + Math.random() * 12, // Doubled again (was 9 + Math.random() * 6)
            sourceId: playerId,
            createdAt: Date.now()
        };

        scene.add(cannonBall);
        cannonBalls.push(cannonBall);
    }
}

// Function to remove other player's boat
function removeOtherPlayerBoat(playerId) {
    const boat = otherPlayers.get(playerId);
    if (boat) {
        // Remove from scene
        scene.remove(boat);

        // Remove from otherPlayers map
        otherPlayers.delete(playerId);
    }
}

// Initialize the game
init();